import * as immutable from "immutable";
import { registerDevtoolsFormatter } from "./DevtoolsFormatters";
import { HashMapFormatter } from "./HashMapDevtoolsFormatter";

export class HashMap<K, V> {
  /**
   * Create a new empty HashMap.
   */
  public static empty<K, V>(): HashMap<K, V> {
    return new HashMap(immutable.Map<K, V>());
  }

  /**
   * Create a new List with elements copied from a source array of Key/Value
   * pairs.
   *
   * If the source array contains multiple elements with the same Key, then it
   * is unspecified which value will be chosen.
   */
  public static fromArray<K, V>(array: [K, V][]): HashMap<K, V> {
    return new HashMap(immutable.Map<K, V>(array));
  }

  /**
   * Returns true if the HashMap is empty
   */
  public isEmpty(): boolean {
    return this.m.size === 0;
  }

  /**
   * Returns the size of the HashMap
   */
  public size(): number {
    return this.m.size;
  }

  /**
   * Returns the specified element, or `undefined` if it does not exist.
   */
  public get(key: K): V | undefined {
    return this.m.get(key);
  }

  /**
   * Sets the specified element to the given value.
   *
   * A new HashMap is always returned (even the old value was the same as the
   * new value)
   */
  public set(key: K, value: V): HashMap<K, V> {
    return new HashMap(this.m.set(key, value));
  }

  /**
   * Returns a new Map which excludes this `key`.
   *
   * Note: `delete` cannot be safely used in IE8, but is provided to mirror
   * the ES6 collection API.
   *
   * <!-- runkit:activate -->
   * ```js
   * const { Map } = require('immutable')
   * const originalMap = Map({
   *   key: 'value',
   *   otherKey: 'other value'
   * })
   * // Map { "key": "value", "otherKey": "other value" }
   * originalMap.delete('otherKey')
   * // Map { "key": "value" }
   * ```
   *
   * Note: `delete` can be used in `withMutations`.
   *
   */
  public delete(key: K): HashMap<K, V> {
    return new HashMap(this.m.delete(key));
  }

  /**
   * True if a key exists within this `Collection`, using `Immutable.is`
   * to determine equality
   */
  public has(key: K): boolean {
    return this.m.has(key);
  }

  /**
   * Update a single value in the HashMap, using a suplied update function.
   *
   * The callback receives the current value in the map (or `undefined` if it
   * is not set), and should return the new value (or `undefined` to delete it
   * from the map).
   *
   * If the callback returns the same value that it received, then the same
   * HashMap will be returned unchanged.
   */
  public update(callback: (value: V | undefined) => V | undefined, key: K): HashMap<K, V> {
    const prev = this.m.get(key);
    const updated = callback(prev);
    if (updated === prev) {
      return this;
    } else if (updated === undefined) {
      return new HashMap(this.m.delete(key));
    } else {
      return new HashMap(this.m.set(key, updated));
    }
  }

  /**
   * Returns all of the keys of the HashMap, as an Iterable.
   *
   * The order is unspecified.
   */
  public keys(): IterableIterator<K> {
    return this.m.keys();
  }

  /**
   * Returns all of the values of the HashMap, as an Iterable
   *
   * The order is unspecified.
   */
  public values(): IterableIterator<V> {
    return this.m.values();
  }

  /**
   * Returns all of the Key/Value pairs of the HashMap, as an Iterable
   *
   * The order is unspecified.
   */
  public entries(): IterableIterator<[K, V]> {
    return this.m.entries();
  }

  private constructor(hmap: immutable.Map<K, V>) {
    this.m = hmap;
  }

  private m: immutable.Map<K, V>;
}

// export namespace HashMap {
//     export function set<K, V>(key: K, value: V): (map: HashMap<K, V>) => HashMap<K, V> {
//         return map => map.set(key, value);
//     }
// }

// export class HashMap<K, V> {
//     public static empty<K, V>(): HashMap<K, V> {
//         return new HashMap(new Map<K, V>());
//     }

//     public static fromArray<K, V>(array: [K, V][]): HashMap<K, V> {
//         return new HashMap(new Map<K, V>(array));
//     }

//     public isEmpty(): boolean {
//         return this.m.size === 0;
//     }

//     public size(): number {
//         return this.m.size;
//     }

//     public get(key: K): V | undefined {
//         return this.m.get(key);
//     }

//     public set(key: K, value: V): HashMap<K, V> {
//         const m2 = new Map<K, V>(<any>this.m);
//         m2.set(key, value);
//         return new HashMap(m2);
//     }

//     public update(callback: (value: V | undefined) => V | undefined, key: K): HashMap<K, V> {
//         const prev = this.m.get(key);
//         const updated = callback(prev);
//         if (updated === prev) {
//             return this;
//         } else  if (updated === undefined) {
//             const m2 = new Map<K, V>(<any>this.m);
//             m2.delete(key);
//             return new HashMap(m2);
//         } else {
//             const m2 = new Map<K, V>(<any>this.m);
//             m2.set(key, updated);
//             return new HashMap(m2);
//         }
//     }

//     // public keys(): IterableIterator<K> {
//     //     return this.m.keys();
//     // }

//     public values(): IterableIterator<V> {
//         const arr: V[] = []
//         this.m.forEach((value: V) => {
//             arr.push(value);
//         });
//         return arr;
//     }

//     // public entries(): IterableIterator<[K, V]> {
//     //     return this.m.entries();
//     // }

//     private constructor(hmap: Map<K, V>) {
//         this.m = hmap;
//     }

//     private m: Map<K, V>;
// }

// export namespace HashMap {
//     // export function set<K, V>(key: K, value: V): (map: HashMap<K, V>) => HashMap<K, V> {
//     //     return map => map.set(key, value);
//     // }
// }

registerDevtoolsFormatter(HashMapFormatter);
